import React, { useState } from "react";

/**
 * CheckboxList — searchable scrollable 2-column checkbox list with optional "Select all".
 * Matches the user's reference design.
 *
 * Props:
 *  - values: string[]
 *  - onChange: (next: string[]) => void
 *  - options: Array<{ value: string; label: string }>
 *  - searchPlaceholder?: string
 *  - showSearch?: boolean                 (default true)
 *  - showSelectAll?: boolean              (default true)
 *  - selectAllLabel?: string              (default "Select All")
 *  - columns?: 1 | 2                      (default 2)
 *  - maxHeight?: string                   (default "240px")
 *  - testId?: string
 */
export default function CheckboxList({
  values = [],
  onChange,
  options = [],
  searchPlaceholder = "Search...",
  showSearch = true,
  showSelectAll = true,
  selectAllLabel = "Select All",
  columns = 2,
  maxHeight = "240px",
  testId,
}) {
  const [query, setQuery] = useState("");

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const allSelected = filtered.length > 0 && filtered.every((o) => values.includes(o.value));

  const toggle = (v) => {
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };

  const toggleAll = () => {
    if (allSelected) {
      const visibleVals = new Set(filtered.map((o) => o.value));
      onChange(values.filter((v) => !visibleVals.has(v)));
    } else {
      const merged = Array.from(new Set([...values, ...filtered.map((o) => o.value)]));
      onChange(merged);
    }
  };

  return (
    <div
      className="rounded-md border border-zinc-200 bg-white p-3"
      data-testid={testId}
    >
      {showSearch && (
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full h-8 px-2 text-sm outline-none border border-zinc-200 rounded focus:border-zinc-950 mb-2"
          data-testid={testId ? `${testId}-search` : undefined}
        />
      )}
      <div
        className={`grid gap-x-4 gap-y-1.5 overflow-auto pr-1 ${
          columns === 2 ? "grid-cols-2" : "grid-cols-1"
        }`}
        style={{ maxHeight }}
      >
        {showSelectAll && filtered.length > 0 && (
          <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer col-span-1">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="h-4 w-4 rounded border-zinc-300 text-red-600 focus:ring-red-500 accent-red-600"
              data-testid={testId ? `${testId}-select-all` : undefined}
            />
            <span className="font-semibold">{selectAllLabel}</span>
          </label>
        )}
        {filtered.length === 0 && (
          <p className="col-span-2 text-xs text-zinc-400 py-2">No options</p>
        )}
        {filtered.map((o) => {
          const checked = values.includes(o.value);
          return (
            <label
              key={o.value}
              className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer"
              data-testid={testId ? `${testId}-option-${o.value}` : undefined}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(o.value)}
                className="h-4 w-4 rounded border-zinc-300 text-red-600 focus:ring-red-500 accent-red-600"
              />
              <span className="truncate">{o.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
